import React, { useState } from 'react';
import { Formik, Field, FieldArray, Form } from 'formik';
import { Row, Col, Button, FormGroup, Label, Input, InputGroup, InputGroupText } from 'reactstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { setActiveFlow, setCurrentStep, setFormData } from 'store/slices/flowSlice';
import CommodityCascader from 'utils/CommodityCascader';
import * as Yup from 'yup'; // Import Yup for validation
import { useTranslation } from "react-i18next";


const calculateTotalWeight = (pieces, weightPerPiece) => {
  let totalWeight = pieces && weightPerPiece ? pieces * weightPerPiece : 0;
  return totalWeight.toFixed(2);
};

const calculateVolume = (length, width, height, numberOfPieces) => {
  let volume = length && width && height * numberOfPieces ? (length * width * height * numberOfPieces) : 0;
  volume = volume * 1e-6;
  volume = volume < 0.01 ? 0.01 : volume;
  return volume.toFixed(2); // Round off to 2 decimal places
};

// Commodity grouped options

const initialItem = {
  pieces: '',
  packageType: '',
  commodity: '',
  weightPerPiece: '',
  totalWeight: '',
  lengthPerPiece: '',
  widthPerPiece: '',
  heightPerPiece: '',
  volumeTotal: '',
  dangerousGoods: false,
  stackable: false,
};

const initialReference = {
  referenceType: '',
  referenceNo: '',
};

const Ship2Domestic = ({ onNext }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Define validation schema
  const validationSchema = Yup.object().shape({
    cargoReadyDate: Yup.date().optional(),
    shipmentRecipient: Yup.object().optional(),
    items: Yup.array().of(
      Yup.object().shape({
        pieces: Yup.number().required(t('Pieces are required')).positive(t('Must be a positive number')).integer(),
        packageType: Yup.object().required(t('Package type is required')).nullable(),
        commodity: Yup.string().required(t('Commodity is required')).nullable(),
        weightPerPiece: Yup.number().required(t('Weight per piece is required')).positive(t('Must be a positive number')),
        lengthPerPiece: Yup.number().required(t('Length per piece is required')).positive(t('Must be a positive number')),
        widthPerPiece: Yup.number().required(t('Width per piece is required')).positive(t('Must be a positive number')),
        heightPerPiece: Yup.number().required(t('Height per piece is required')).positive(t('Must be a positive number')),
      })
    ),
    references: Yup.array().of(
      Yup.object().shape({
        referenceType: Yup.object().optional(),
        referenceNo: Yup.string().optional(),
      })
    ),
  });
  const packageTypes = [
    { value: 'europallet', label: t('Europallet') },
    { value: 'carton', label: t('Carton') },
    { value: 'one-way-pallet', label: t('One-way pallet') },
    { value: 'crate', label: t('Crate') },
    { value: 'bundle', label: t('Bundle') },
  ];

  const recipientOptions = [
    { value: 'business', label: t('Business/Company') },
    { value: 'private', label: t('Private Home') },
  ];

  const referenceOptions = [
    { value: 'shippers-reference', label: t('Shipper\'s Reference No.') },
    { value: 'consignees-reference', label: t('Consignee\'s Reference No.') },
  ];


  const [cargoReadyDate, setCargoReadyDate] = useState(new Date());


  const handleSubmit = (values) => {
    dispatch(setFormData(values));
    dispatch(setActiveFlow('domestic'));
    dispatch(setCurrentStep('2'));
    onNext();
  };

  return (
    <div className="e2g-shipping-step2-form">
      <div className="e2g-shipping-step2-container container">
        <div className="e2g-shipping-step2-form-wrap">
          <Formik
            initialValues={{
              items: [initialItem],
              references: [initialReference],
              cargoReadyDate: new Date(),
              note: null,
              shipmentRecipient: '',
              dangerousGoods: false,
              stackable: false,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className='e2g-form-step e2g-domestic-step form-step'>
                <h4 className="e2g-step2-head mb-2" style={{ fontSize: '32px' }}>
                  {t("What are the cargo details?")}
                </h4>
                <FieldArray
                  name="items"
                  render={arrayHelpers => (
                    <div className="e2g-shipping-field-array-wrap">
                      {values.items && values.items.length > 0 ? (
                        values.items.map((item, index) => (
                          <div key={index} className="e2g-shipping-field-item">
                            <Row className="e2g-shipping-field-array mb-3">
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.pieces`}>{t("Pieces")}</Label>
                                  <Field
                                    className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts"
                                    name={`items.${index}.pieces`}
                                    type="number"
                                    as={Input}
                                    min="0" // Prevent negative values
                                    onChange={(e) => {
                                      const pieces = Math.max(0, e.target.value); // Ensure positive numbers
                                      setFieldValue(`items.${index}.pieces`, pieces);
                                      const totalWeight = calculateTotalWeight(pieces, values.items[index].weightPerPiece);
                                      setFieldValue(`items.${index}.totalWeight`, totalWeight);
                                      const volumeTotal = calculateVolume(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, pieces);
                                      setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                    }}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].pieces && touched.items && touched.items[index] && touched.items[index].pieces ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].pieces}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="4">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.packageType`}>{t("Package Type")}</Label>
                                  <Select
                                    className='bg-light e2g-custom-height e2g-custom-fonts'
                                    id={`items.${index}.packageType`}
                                    options={packageTypes}
                                    onChange={option => setFieldValue(`items.${index}.packageType`, option)}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].packageType && touched.items && touched.items[index] && touched.items[index].packageType ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].packageType}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="4">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.commodity`}>{t("Commodity")}</Label>
                                  <CommodityCascader block
                                    searchable={false}
                                    size="lg"
                                    onChange={option => setFieldValue(`items.${index}.commodity`, option)}
                                    placeholder={t("Select commodity")}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].commodity && touched.items && touched.items[index] && touched.items[index].commodity ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].commodity}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.weightPerPiece`}>{t("Weight/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.weightPerPiece`}
                                      type="number"
                                      as={Input}
                                      min="0" // Prevent negative values
                                      step="0.01"
                                      onChange={(e) => {
                                        const weightPerPiece = Math.max(0, e.target.value); // Ensure positive numbers
                                        setFieldValue(`items.${index}.weightPerPiece`, weightPerPiece);
                                        const totalWeight = calculateTotalWeight(values.items[index].pieces, weightPerPiece);
                                        setFieldValue(`items.${index}.totalWeight`, totalWeight);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("kg")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].weightPerPiece && touched.items && touched.items[index] && touched.items[index].weightPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].weightPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.totalWeight`}>{t("Weight")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.totalWeight`}
                                      type="number"
                                      as={Input}
                                      readOnly
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("kg")}</InputGroupText>
                                  </InputGroup>
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.lengthPerPiece`}>{t("Length/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.lengthPerPiece`}
                                      type="number"
                                      as={Input}
                                      min="0" // Prevent negative values
                                      onChange={(e) => {
                                        const lengthPerPiece = Math.max(0, e.target.value); // Ensure positive numbers
                                        setFieldValue(`items.${index}.lengthPerPiece`, lengthPerPiece);
                                        const volumeTotal = calculateVolume(lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces);
                                        setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("cm")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].lengthPerPiece && touched.items && touched.items[index] && touched.items[index].lengthPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].lengthPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.widthPerPiece`}>{t("Width/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.widthPerPiece`}
                                      type="number"
                                      as={Input}
                                      min="0" // Prevent negative values
                                      onChange={(e) => {
                                        const widthPerPiece = Math.max(0, e.target.value); // Ensure positive numbers
                                        setFieldValue(`items.${index}.widthPerPiece`, widthPerPiece);
                                        const volumeTotal = calculateVolume(values.items[index].lengthPerPiece, widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces);
                                        setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("cm")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].widthPerPiece && touched.items && touched.items[index] && touched.items[index].widthPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].widthPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.heightPerPiece`}>{t("Height/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height border-0 rounded-0'
                                      name={`items.${index}.heightPerPiece`}
                                      type="number"
                                      as={Input}
                                      min="0" // Prevent negative values
                                      onChange={(e) => {
                                        const heightPerPiece = Math.max(0, e.target.value); // Ensure positive numbers
                                        setFieldValue(`items.${index}.heightPerPiece`, heightPerPiece);
                                        const volumeTotal = calculateVolume(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, heightPerPiece, values.items[index].pieces);
                                        setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("cm")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].heightPerPiece && touched.items && touched.items[index] && touched.items[index].heightPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].heightPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.volumeTotal`}>{t("Volume (total)")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='e2g-form-field
                                   bg-light custom-height border-0 rounded-0'
                                      name={`items.${index}.volumeTotal`}
                                      type="number"
                                      as={Input}
                                      readOnly
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("m³")}</InputGroupText>
                                  </InputGroup>
                                </FormGroup>
                              </Col>
                              <div className="e2g-push-icons d-flex align-items-end flex-row-reverse">
                                <div className="d-flex align-items-end gap-2 mb-3">
                                  <Button color="secondary" className='p-1 d-flex' onClick={() => arrayHelpers.insert(index, item)}>
                                    <FontAwesomeIcon icon={faCopy} />
                                  </Button>
                                  <Button color="primary" className='p-1 d-flex' onClick={() => arrayHelpers.push(initialItem)}>
                                    <FontAwesomeIcon icon={faPlus} />
                                  </Button>
                                  {values.items.length > 1 && (
                                    <Button color="danger" className='p-1 d-flex' onClick={() => arrayHelpers.remove(index)}>
                                      <FontAwesomeIcon icon={faMinus} />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </Row>
                          </div>
                        ))
                      ) : (
                        <Button type="button" onClick={() => arrayHelpers.push(initialItem)}>
                          {t("Add an item")}
                        </Button>
                      )}
                    </div>
                  )}
                />
                <div style={{ background: '#cae7ea', padding: '.85rem .5rem' }} className="d-flex justify-content-between align-items-center gap-2 e2g-total-value">
                  <h6 className='fw-bold m-0'>{t("Total")}</h6>
                  <div className='d-flex gap-4 e2g-total-value-cont'>
                    <p className='m-0'>{t("Pieces")}: {values.items.reduce((sum, item) => sum + Number(item.pieces || 0), 0)}</p>
                    <p className='m-0'>{t("Weight")}: {values.items.reduce((sum, item) => sum + Number(item.totalWeight || 0), 0)} {t("kg")}</p>
                    <p className='m-0'>{t("Volume")}: {values.items.reduce((sum, item) => sum + Number(item.volumeTotal || 0), 0)} {t("cm³")}</p>
                  </div>
                </div>
                <div className='e2g-pickup-recipent d-flex w-75 gap-3'>
                  <FormGroup className="d-flex mb-0 col-6 align-items-start gap-3 flex-column">
                    <Label className='col-5 m-0 d-flex align-items-center' for="cargoReadyDate">{t("Cargo Ready Date")}</Label>
                    <DatePicker
                      id="cargoReadyDate"
                      calendarClassName="e2g-calendar"
                      selected={cargoReadyDate}
                      onChange={date => {setCargoReadyDate(date); setFieldValue('cargoReadyDate', date)}}
                      className="e2g-ready-date bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control w-100"
                    />
                    {errors.cargoReadyDate && touched.cargoReadyDate ? (
                      <div className="e2g-error-text text-danger">{errors.cargoReadyDate}</div>
                    ) : null}
                  </FormGroup>
                  <FormGroup className='d-flex mb-0 col-6 align-items-start gap-3 flex-column'>
                    <Label className="e2g-cargo-form-label col-5 m-0 d-flex align-items-center" for="shipmentRecipient">{t("Shipment Recipient")}</Label>
                    <Select
                      className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control p-0 w-100"
                      id="shipmentRecipient"
                      options={recipientOptions}
                      placeholder={t("Select...")}
                      onChange={option => setFieldValue('shipmentRecipient', option)}
                    />
                    {errors.shipmentRecipient && touched.shipmentRecipient ? (
                      <div className="e2g-error-text text-danger">{errors.shipmentRecipient}</div>
                    ) : null}
                  </FormGroup>
                </div>
                <div className='e2g-note d-flex w-75 gap-3'>
                  <FormGroup className="d-flex mb-0 col-12 align-items-start gap-3 flex-column">
                    <Label className='col-5 m-0 d-flex align-items-center' for="cargoReadyDate">{t("Note")}</Label>
                    <Field
                      className="e2g-note e2g-form-field bg-light custom-height rounded-0 border-0 custom-fonts form-control  w-100"
                      name={`note`}
                      type="textarea"
                      as={Input}
                    />
                    {errors.note && touched.note ? (
                      <div className="e2g-error-text text-danger">{errors.note}</div>
                    ) : null}
                  </FormGroup>
                </div>
                <FieldArray name="references">
                  {({ push, remove }) => (
                    <>
                      {values.references.map((reference, index) => (
                        <Row key={index} className="mb-3 e2g-referrance">
                          <Col sm="5">
                            <FormGroup className='d-flex mb-0 col-6 align-items-start gap-3 flex-column w-100'>
                              <Label className="e2g-cargo-form-label m-0 d-flex align-items-center" for={`references.${index}.referenceType`} >{t("Reference")}</Label>
                              <Select
                                className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control p-0 w-100"
                                id={`references.${index}.referenceType`}
                                options={referenceOptions}
                                placeholder={t("Select...")}
                                onChange={option => setFieldValue(`references.${index}.referenceType`, option)}
                              />
                              {errors.references && errors.references[index] && errors.references[index].referenceType && touched.references && touched.references[index] && touched.references[index].referenceType ? (
                                <div className="e2g-error-text text-danger">{errors.references[index].referenceType}</div>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col sm="5">
                            <FormGroup className='d-flex mb-0 col-6 align-items-start gap-3 flex-column w-100'>
                              <Label className="e2g-cargo-form-label m-0 d-flex align-items-center" for={`references.${index}.referenceNo`} >{t("Reference No.")}</Label>
                              <Field
                                className=" e2g-form-field bg-light custom-height rounded-0 border-0 custom-fonts form-control  w-100"
                                name={`references.${index}.referenceNo`}
                                type="text"
                                as={Input}
                              />
                              {errors.references && errors.references[index] && errors.references[index].referenceNo && touched.references && touched.references[index] && touched.references[index].referenceNo ? (
                                <div className="e2g-error-text text-danger">{errors.references[index].referenceNo}</div>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col sm="2" className="d-flex align-items-end mt-3 mb-3 ">
                            <Button color="primary" onClick={() => push(initialReference)}>
                              <FontAwesomeIcon icon={faPlus} />
                            </Button>
                            {values.references.length > 1 && (
                              <Button color="danger" onClick={() => remove(index)}>
                                <FontAwesomeIcon icon={faMinus} />
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                    </>
                  )}
                </FieldArray>
                <Button type="submit" color="success" className="btn btn-success w-25 p-3 mb-3" style={{ margin: '0;' }}>{t("Submit")}</Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Ship2Domestic;
